<template>
  <div class="notification-strategy-config">
    <!-- 操作按钮 -->
    <div class="action-btn">
      <el-button
        type="primary"
        size="small"
        icon="el-icon-plus"
        @click="addStrategy"
      >
        {{ $t("basicData.AddRow") }}
      </el-button>
    </div>

    <!-- 策略配置表格 -->
    <el-table
      :data="strategyList"
      border
      max-height="350"
    >
      <!-- 通知维度列 -->
      <el-table-column
        :label="$t('basicData.NotificationDimension')"
        min-width="150"
      >
        <template #default>
          <span>{{ $t("basicData.RemainingAvailableAmount") }}</span>
        </template>
      </el-table-column>

      <!-- 条件列 -->
      <el-table-column
        :label="$t('basicData.Condition')"
        width="120"
        align="center"
      >
        <template #default>
          <span>&lt;</span>
        </template>
      </el-table-column>

      <!-- 值列 -->
      <el-table-column min-width="180">
        <template #header>
          <span>
            <span style="color: #f56c6c">*</span>
            {{ $t("basicData.Value") }}
          </span>
        </template>
        <template #default="{ row }">
          <el-input-number
            v-model="row.thresholdAmount"
            :precision="2"
            :step="1"
            :min="0"
            :controls="false"
            size="mini"
            style="width: 100%"
            :placeholder="$t('basicData.PleaseEnter')"
          />
        </template>
      </el-table-column>

      <!-- 操作列 -->
      <el-table-column
        :label="$t('basicData.operate')"
        width="120"
        align="center"
        fixed="right"
      >
        <template #default="{ row, $index }">
          <el-popconfirm
            :title="$t('basicData.AreYouSureDelete')"
            @confirm="deleteStrategy(row, $index)"
          >
            <template slot="reference">
              <el-button type="danger" size="mini">
                {{ $t("basicData.Delete") }}
              </el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <!-- 操作按钮 -->
    <div class="operate-button">
      <el-button
        size="small"
        @click="cancel"
      >{{ $t('basicData.Cancel') }}</el-button>

      <el-button
        size="small"
        type="primary"
        :loading="saving"
        style="margin: 0"
        @click="save"
      >{{ $t('basicData.Save') }}</el-button>
    </div>
  </div>
</template>

<script>
import { apiFundNotice, apiFundNoticeDelete, apiFundNoticeSave } from '@/api/finance/fundManagement';

const NOTICE_TYPE = {
  REMAINING_AMOUNT: 1 // 剩余可用金额
};

// 条件类型常量
const CONDITION_TYPE = {
  LESS_THAN: 'LESS_THAN' // 小于
};

export default {
  name: 'NotificationStrategyConfig',

  props: {
    // 后续针对单个客户配置
    customerId: {
      type: String,
      default: ''
    },
    // 弹窗显示状态
    visible: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // 策略列表
      strategyList: [],
      // 保存中状态
      saving: false,
      // 加载中状态
      loading: false
    };
  },

  watch: {
    // 监听弹窗打开，每次打开都重新加载数据
    visible(newVal) {
      if (newVal) {
        this.loadStrategyList();
      }
    }
  },

  created() {
    this.loadStrategyList();
  },

  methods: {
    /**
     * 新增策略
     */
    addStrategy() {
      // 检查是否已有未填写值的行
      const hasEmptyRow = this.strategyList.some(
        item => item.thresholdAmount === null || item.thresholdAmount === undefined
      );

      if (hasEmptyRow) {
        this.$message.warning(this.$t('basicData.PleaseCompleteTheCurrentLineFirst'));
        return;
      }

      this.strategyList.push({
        id: null, // 新增时无ID
        customerId: this.customerId || null,
        noticeType: NOTICE_TYPE.REMAINING_AMOUNT,
        conditionType: CONDITION_TYPE.LESS_THAN,
        thresholdAmount: null
      });
    },

    /**
     * 删除策略
     * @param {Object} row - 行数据
     * @param {Number} index - 索引
     */
    async deleteStrategy(row, index) {
      try {
        // 如果是已保存的策略（有ID），需要调用删除接口
        if (row.id) {
          // TODO: 取消注释并实现实际删除接口
          await apiFundNoticeDelete(row.id);
          console.log('删除已保存的策略:', row.id);
        }

        // 本地删除
        this.strategyList.splice(index, 1);
        this.$message.success(this.$t('basicData.TheOperationSucceeded'));
      } catch (error) {
        console.error('删除策略失败:', error);
        this.$message.error(this.$t('basicData.failed'));
      }
    },

    /**
     * 初始化加载策略配置数据
     */
    async loadStrategyList() {
      this.loading = true;
      try {
        // TODO: 取消注释并实现实际查询接口
        const { data } = await apiFundNotice({
          customerId: this.customerId
        });
        if (data && data.length > 0) {
          this.strategyList = data.map(item => ({
            ...item,
            thresholdAmount: Number(item.thresholdAmount)
          }));
        }
        console.log('加载策略列表, customerId:', this.customerId);
      } catch (error) {
        console.error('加载消息通知策略配置失败:', error);
        this.strategyList = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * 取消操作
     */
    cancel() {
      // 重新加载原始数据
      this.strategyList = [];
      this.$emit('close');
    },

    /**
     * 保存策略配置
     */
    async save() {
      // 1. 校验列表是否为空
      if (this.strategyList.length === 0) {
        this.$message.warning(this.$t('basicData.StrategyConfigurationDetailsCannotBeEmpty'));
        return;
      }

      // 2. 校验是否有空值
      const emptyRows = this.strategyList.filter(
        item => item.thresholdAmount === null || item.thresholdAmount === undefined
      );

      if (emptyRows.length > 0) {
        this.$message.warning(this.$t('basicData.ValueCannotBeEmpty'));
        return;
      }

      // 3. 校验值是否重复
      const valueSet = new Set();
      const duplicateValues = [];

      for (const item of this.strategyList) {
        if (valueSet.has(item.thresholdAmount)) {
          duplicateValues.push(item.thresholdAmount);
        } else {
          valueSet.add(item.thresholdAmount);
        }
      }

      if (duplicateValues.length > 0) {
        this.$message.warning(
          `${this.$t('basicData.ValueCannotBeRepeated')}: ${duplicateValues.join(', ')}`
        );
        return;
      }

      // 4. 校验最小值
      const invalidRows = this.strategyList.filter(
        item => item.thresholdAmount <= 0
      );

      if (invalidRows.length > 0) {
        this.$message.warning(this.$t('basicData.MustBeGreaterThan0'));
        return;
      }

      // 5. 执行保存
      this.saving = true;
      try {
        // TODO: 取消注释并实现实际保存接口
        const submitData = this.strategyList.map(item => ({
          id: item.id,
          customerId: this.customerId,
          noticeType: item.noticeType,
          conditionType: item.conditionType,
          thresholdAmount: item.thresholdAmount
        }));
        await apiFundNoticeSave(submitData);

        console.log('保存策略配置:', this.strategyList);
        this.$message.success(this.$t('basicData.TheOperationSucceeded'));

        this.cancel();
      } catch (error) {
        console.error('保存策略配置失败:', error);
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.notification-strategy-config {

  .action-btn {
    margin: 10px 0;
  }
}

.operate-button {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 10px;
}
</style>
